import { debounce } from 'lodash'
import { createBackoffPoller } from 'modules/common/utils/poll-with-backoff'
import { apiCall } from 'modules/core/services/api/api'
import { CreativeService } from 'openapi/services/CreativeService'
import { ProjectService } from 'openapi/services/ProjectService'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { create } from 'zustand'
import { CREATOR_MODE_MAP, type Creative, type VideoStatus } from '../types'
import { entityToCreative, flushPersistsAndWait, useCreativeStore } from './creative-store'
import { useWeeklyQueueStore } from '../../autopilot/store/weekly-queue-store'
import { livePlatformToSocial, PUBLISH_METADATA_KEY } from '../utils/publish-creative'
import { isFailedVideo } from '../previews/preview-utils'

// ─── Post-as-cluster shape mirror ─────────────────────────────────────────
// These mirror @mondaygpt/types/creative/content-shape.ts. Inlined to avoid
// races with the workspace package build during dev.
//
// `'x'` is the one deliberate divergence from the package type: the topic
// grid still lists X as "coming soon" (no idea fan-out to X), but the server
// promotes X into `candidatePlatforms` / `primaryPlatform` for any post whose
// primary IS an X post (assistant-created, API-created, or an X leg added to
// a post), so this client-side union must admit it or every
// `Record<LivePlatform, …>` map silently lacks the entry: that is exactly how
// the PostModal rendered X as an infinite skeleton until 2026-09-06.
export type LivePlatform =
  'instagram' | 'facebook' | 'tiktok' | 'linkedin' | 'youtube_shorts' | 'pinterest' | 'x'

export type ContentShape =
  'static_image' | 'short_vertical_video' | 'story' | 'carousel' | 'long_form_video'

export type IdeaLens = 'audience' | 'cultural' | 'brand'

export interface PostProjectData {
  kind: 'post'
  idea: string
  shape: ContentShape
  /** @deprecated Lens classification dropped in topic redesign. */
  lens?: IdeaLens
  primaryCreativeId: string | null
  primaryPlatform: LivePlatform
  candidatePlatforms: LivePlatform[]
  comingSoonPlatforms: string[]
  /** Platforms this Post publishes to. */
  confirmedPlatforms: LivePlatform[]
  /** Origin of this Post. 'home' = Topics + Ideas grid (default), 'autopilot' = Weekly Cockpit, 'intent' = Home hero creation moment. */
  source?: 'home' | 'autopilot' | 'intent'
  /** Topic this Post belongs to. New flow always sets this; legacy may use narrativeId. */
  topicId?: string
  /**
   * Monotonic revision of the canonical (primary) creative's content. Bumped on
   * every user-initiated canonical edit; pinned siblings compare their
   * `derivedFromRevision` against it for the "post changed" staleness chip.
   */
  contentRevision?: number
  /** @deprecated Use topicId. Dual-written during cutover. */
  narrativeId?: string
  /** @deprecated Headline lives on the Topic now. */
  narrativeHeadline?: string
  /** @deprecated Position lives on the Topic now. */
  narrativeIndex?: number
  /** @deprecated Topic.batchId supersedes. */
  narrativeBatchId?: string
  /** @deprecated Topic.generatedAt supersedes. */
  narrativeGeneratedAt?: string
}

function isPostProjectData(data: unknown): data is PostProjectData {
  return typeof data === 'object' && data !== null && (data as { kind?: unknown }).kind === 'post'
}

// Topic-redesign cap: only the 3 shapes for the picked topic exist; lazy-
// generated topics may have 0..3 cards in flight.
const POSTS_PER_TOPIC = 3

// Pusher fallback. If `PostSuggestionReady` / `PostSuggestionsComplete`
// events drop (network blip, browser tab unfocused), this backoff catches
// up by refetching ideas. Series total = 77s; cancelled the moment Pusher
// fires Complete or a refetch lands a full batch.
const ideaPoller = createBackoffPoller([2_000, 5_000, 10_000, 20_000, 40_000])

/**
 * Per-platform publishing state stamped by PostPublishProgressDialog as
 * sync results land and Pusher fires for async platforms (TikTok/YouTube).
 * Once every targeted platform reaches a terminal state, `idea.status`
 * flips to `'live'`.
 */
export interface IdeaPlatformResult {
  creativeId: string
  state: 'pending' | 'success' | 'failed'
  permalink?: string
  error?: string
  retryable?: boolean
}

/**
 * Client-side status for a Post idea on Home. Lifecycle is derived (the server
 * no longer carries a `status` enum) from the primary creative's
 * `scheduledAt`/`publishedAt`:
 *   - `'draft'` — alive, not scheduled, not published.
 *   - `'scheduled'` — a future `scheduledAt` is set and we haven't published yet.
 *   - `'live'` — every targeted platform terminated / a `publishedAt` is set.
 *   - `'publishing'` — transient, while PostPublishProgressDialog is fanning out
 *     per-platform publishes.
 */
export type IdeaStatus = 'draft' | 'scheduled' | 'publishing' | 'live'

/** Front-end view of one Post idea suggestion. */
export interface PostIdea {
  projectId: string
  idea: string
  shape: ContentShape
  /** @deprecated Lens dropped in topic redesign — kept on PostIdea for type-shape compat. */
  lens?: IdeaLens
  primaryPlatform: LivePlatform
  candidatePlatforms: LivePlatform[]
  comingSoonPlatforms: string[]
  /**
   * Platforms this Post publishes to. Set when the user has gone through
   * PostModal at least once (or, for autopilot slots, born scheduled at
   * generation time). Empty/undefined for never-confirmed Posts; consumers
   * should fall back to `[primaryPlatform]` when surfacing "where will this
   * publish".
   */
  confirmedPlatforms?: LivePlatform[]
  status: IdeaStatus
  primaryCreative: Creative | null
  /** True while a regenerate request is in flight for this Post. */
  isRegenerating: boolean
  /** Topic this Post belongs to. Falls back to legacy narrativeId during cutover. */
  topicId: string | null
  /** Origin of the Post. 'intent' = created from the Home hero's creation moment. */
  source?: PostProjectData['source']
  /** Set when every targeted platform terminates (success or failure). */
  publishedAt?: string
  /** Future ISO datetime when the post will publish — set after confirm-with-schedule. */
  scheduledAt?: string
  /** Per-platform publish state for the Live button + retry flow. */
  platformResults?: Partial<Record<LivePlatform, IdeaPlatformResult>>
  /**
   * Whether this post is subject to the approval gate (review-mode autopilot),
   * snapshotted on the post. Paired with `approvedAt`: needsApproval =
   * requiresApproval && !approvedAt. False/undefined for user-generated posts.
   */
  requiresApproval?: boolean
  /** When the post was approved for publishing (null/undefined = not approved). */
  approvedAt?: string | null
  /**
   * Monotonic revision of the canonical's content (server
   * `PostProjectData.contentRevision`). Compared against a pinned sibling's
   * `derivedFromRevision` to surface "behind the post". Bumped optimistically
   * on local canonical edits; the server value lands on the next fetch.
   */
  contentRevision?: number
}

interface IdeaPreserveFields {
  isRegenerating?: boolean
  /** Local 'publishing' / 'live' / 'scheduled' status survives refetches even
   * though the derived server state would otherwise read as a plain draft. */
  status?: IdeaStatus
  publishedAt?: string
  scheduledAt?: string
  platformResults?: PostIdea['platformResults']
}

/**
 * Reconstruct per-platform publish state from the project's creatives'
 * server-side `content.{platform}Publish` metadata. Used on hard refresh —
 * the local 'publishing'/'live' flag is gone, but each successful creative
 * still carries `lastPublishedAt` + `permalink`, which is enough to rebuild
 * the IdeaCard Live button + dropdown.
 */
function reconstructPlatformResultsFromServer(
  projectCreatives: Array<Parameters<typeof entityToCreative>[0]>,
  confirmedPlatforms: LivePlatform[]
): { platformResults: PostIdea['platformResults']; latestPublishedAt: string | null } {
  const results: PostIdea['platformResults'] = {}
  let latestPublishedAt: string | null = null

  for (const platform of confirmedPlatforms) {
    // Find the creative serving this platform via its CreativeType (e.g.
    // 'instagram_post'), which lives on `data.creativeType`. The entity-level
    // `type` column is a coarser bucket (e.g. 'instagram') and isn't a key
    // in CREATIVE_TYPE_TO_PLATFORM — looking it up there returns undefined
    // and silently breaks the rebuild.
    const entity = projectCreatives.find(c => {
      const creativeType = (c.data as Record<string, unknown> | null | undefined)?.creativeType as
        string | undefined
      return creativeType ? CREATIVE_TYPE_TO_PLATFORM[creativeType] === platform : false
    })
    if (!entity) continue

    const channel = livePlatformToSocial(platform)
    if (!channel) continue
    const metaKey = PUBLISH_METADATA_KEY[channel]
    // The raw JSONB blob (`entity.data`) wraps the user-visible payload under
    // a nested `content` key; per-platform publish metadata lives there
    // (e.g. data.content.instagramPublish), not at the top level.
    const data = entity.data as Record<string, unknown> | null | undefined
    const content = (data?.content as Record<string, unknown> | null | undefined) ?? null
    const meta =
      (content?.[metaKey] as { lastPublishedAt?: string; permalink?: string } | undefined) ??
      undefined

    const entityPublishedAt = (entity as { publishedAt?: string | null }).publishedAt ?? null

    if (meta?.lastPublishedAt) {
      results[platform] = {
        creativeId: entity.id,
        state: 'success',
        permalink: meta.permalink,
      }
      if (!latestPublishedAt || meta.lastPublishedAt > latestPublishedAt) {
        latestPublishedAt = meta.lastPublishedAt
      }
    } else if (entityPublishedAt) {
      // The publish-metadata block is gone (e.g. a composer edit after publish
      // wiped data.content.{platform}Publish) but the `publishedAt` column
      // survives — the post is genuinely live. Honor the column so /posts agrees
      // with the Composer/Cockpit; the permalink is simply unavailable.
      results[platform] = {
        creativeId: entity.id,
        state: 'success',
        permalink: meta?.permalink,
      }
      if (!latestPublishedAt || entityPublishedAt > latestPublishedAt) {
        latestPublishedAt = entityPublishedAt
      }
    }
    // Neither metadata nor a publishedAt column → not published yet (could be
    // scheduled, or post-now that failed silently). We don't fabricate a
    // 'failed' entry here since we can't tell the difference. The IdeaCard
    // simply omits the platform from the dropdown.
  }

  return { platformResults: results, latestPublishedAt }
}

function projectToIdea(
  p: {
    id: string
    data?: Record<string, unknown> | null
    creatives?: Array<Parameters<typeof entityToCreative>[0]>
    /** Self-describing approval flags off the Project entity. */
    requiresApproval?: boolean
    approvedAt?: string | null
  },
  preserve?: IdeaPreserveFields
): PostIdea | null {
  if (!isPostProjectData(p.data)) return null
  const data = p.data as PostProjectData
  const projectCreatives = p.creatives ?? []

  const primaryEntity = projectCreatives.find(c => c.id === data.primaryCreativeId) ?? null

  // Local 'publishing'/'live'/'scheduled' wins (an in-flight or just-completed
  // PostModal flow still owns the truth). Otherwise derive the lifecycle from
  // the creatives: rebuild live state from server publish metadata; failing
  // that, infer scheduled state from a future creative scheduledAt; otherwise
  // it's a plain draft. The server already scopes the list to alive posts, so
  // there's no per-project status gate to clear before deriving.
  const transientStatuses = new Set<IdeaStatus>(['publishing', 'live', 'scheduled'])
  let status: IdeaStatus =
    preserve?.status && transientStatuses.has(preserve.status) ? preserve.status : 'draft'
  let platformResults = preserve?.platformResults
  let publishedAt = preserve?.publishedAt
  let scheduledAt = preserve?.scheduledAt

  if (!platformResults) {
    const { platformResults: rebuilt, latestPublishedAt } = reconstructPlatformResultsFromServer(
      projectCreatives,
      data.confirmedPlatforms ?? []
    )
    if (rebuilt && Object.keys(rebuilt).length > 0) {
      platformResults = rebuilt
      publishedAt = latestPublishedAt ?? undefined
      status = 'live'
    } else if (status !== 'scheduled') {
      // No publish metadata yet — check if a future schedule is pending.
      const now = Date.now()
      const futureSchedules = projectCreatives
        .map(c => (c as { scheduledAt?: string | null }).scheduledAt)
        .filter((s): s is string => !!s && new Date(s).getTime() > now)
        .sort()
      if (futureSchedules.length > 0) {
        status = 'scheduled'
        scheduledAt = futureSchedules[0]
      }
    }
  }

  const idea: PostIdea = {
    projectId: p.id,
    idea: data.idea,
    shape: data.shape,
    lens: data.lens,
    primaryPlatform: data.primaryPlatform,
    candidatePlatforms: data.candidatePlatforms,
    comingSoonPlatforms: data.comingSoonPlatforms,
    confirmedPlatforms: data.confirmedPlatforms,
    status,
    primaryCreative: primaryEntity ? entityToCreative(primaryEntity) : null,
    isRegenerating: preserve?.isRegenerating ?? false,
    topicId: data.topicId ?? data.narrativeId ?? null,
    source: data.source,
    publishedAt,
    scheduledAt,
    platformResults,
    requiresApproval: p.requiresApproval ?? false,
    approvedAt: p.approvedAt ?? null,
    contentRevision: typeof data.contentRevision === 'number' ? data.contentRevision : undefined,
  }
  return idea
}

/**
 * Build the siblings map for one project from its already-loaded creatives.
 * The list endpoints (/v1/topics, /v1/projects?kind=post) eagerly join all
 * creatives, but `projectToIdea` only extracts the primary — pre-populating
 * `siblingsByProject` from the same payload means PostModal renders
 * previously-generated previews instantly after a refresh, instead of
 * flashing the skeleton while previewSiblings round-trips.
 *
 * Siblings are tagged with `data.seedFromCreativeId === primaryCreativeId`
 * (set in spawnSiblingsInternal); anything else on the project is ignored.
 */
function extractSiblings(
  projectCreatives: Array<Parameters<typeof entityToCreative>[0]>,
  primaryCreativeId: string
): Partial<Record<LivePlatform, Creative>> {
  const byPlatform: Partial<Record<LivePlatform, Creative>> = {}
  for (const entity of projectCreatives) {
    if (entity.id === primaryCreativeId) continue
    const seedFrom = (entity.data as { seedFromCreativeId?: string } | undefined)
      ?.seedFromCreativeId
    if (seedFrom !== primaryCreativeId) continue
    const creative = entityToCreative(entity)
    const platform = creativeTypeToPlatform(creative.type)
    if (platform) byPlatform[platform] = creative
  }
  return byPlatform
}

/**
 * Build `{ projectId → ProjectSiblingsState }` for every project in the
 * payload. Skips projects with no primary or no harvested siblings so the
 * resulting patch stays minimal.
 */
function siblingsPatchFromProjects(
  projects: Array<{
    id: string
    data?: Record<string, unknown> | null
    creatives?: Array<Parameters<typeof entityToCreative>[0]>
  }>
): Record<string, ProjectSiblingsState> {
  const patch: Record<string, ProjectSiblingsState> = {}
  for (const p of projects) {
    if (!isPostProjectData(p.data)) continue
    const primaryId = (p.data as PostProjectData).primaryCreativeId
    if (!primaryId) continue
    const byPlatform = extractSiblings(p.creatives ?? [], primaryId)
    if (Object.keys(byPlatform).length === 0) continue
    patch[p.id] = { byPlatform, loading: false }
  }
  return patch
}

/**
 * Look up the suggested ideas for a topic, sorted chronologically. Project
 * IDs are ULIDs (time-ordered), so ascending sort gives oldest-first —
 * which means each "More posts" tap appends new cards at the end.
 */
export function selectIdeasForTopic(ideas: PostIdea[], topicId: string | null): PostIdea[] {
  if (!topicId) return []
  return ideas
    .filter(i => i.topicId === topicId)
    .sort((a, b) => a.projectId.localeCompare(b.projectId))
}

// Intentionally NO content/primary-creative filter. Posts without a primary
// (race window where creative isn't joined yet) still render as cards with
// a "Generating preview…" placeholder rather than disappearing entirely.

// Tracks an in-flight fetch separately from `loading` so the early-return
// guard does not collide with the initial `loading: true` state used to
// render skeletons on first mount.
let fetchInflight = false

/**
 * A reel whose poster has landed but whose playable video hasn't yet — and
 * that hasn't terminally failed. The in-house render reports `videoStatus:
 * 'ready'` minutes before the mp4/`videoUrl` persists, so the fallback poll
 * must stay alive through this window (the durable delivery is the
 * CreativeContentUpdated Pusher event; this guards against it being dropped).
 */
function reelStillPending(idea: PostIdea): boolean {
  if (idea.shape !== 'short_vertical_video') return false
  const content = idea.primaryCreative?.content as
    { videoUrl?: string | null; videoStatus?: VideoStatus } | undefined
  // videoUrl is falsy here (guarded above): pending = not terminally failed.
  if (!content || content.videoUrl) return false
  return !isFailedVideo({ videoStatus: content.videoStatus })
}

const debouncedFetchImpl = debounce(async () => {
  if (fetchInflight) return
  fetchInflight = true
  useIdeaStore.setState({ loading: true })
  try {
    const projects = await apiCall(ProjectService.projectControllerFindAllV1, null, 'post')
    // Preserve the in-flight regenerate flag across refetches. The flag is
    // cleared when the PostSuggestionReady Pusher event fires (see setIdeaReady)
    // — NOT by polling, since the server swaps primaryCreativeId immediately
    // and only fills in the content ~60s later.
    const prev = new Map(
      useIdeaStore.getState().ideas.map(i => [
        i.projectId,
        {
          isRegenerating: i.isRegenerating,
          status: i.status,
          publishedAt: i.publishedAt,
          scheduledAt: i.scheduledAt,
          platformResults: i.platformResults,
        },
      ])
    )
    const projectInputs = projects.map(p => ({
      id: p.id,
      data: p.data as Record<string, unknown> | null | undefined,
      creatives: (p as { creatives?: Parameters<typeof entityToCreative>[0][] }).creatives,
      requiresApproval: (p as { requiresApproval?: boolean }).requiresApproval,
      approvedAt: (p as { approvedAt?: string | null }).approvedAt,
    }))
    // Keep every successfully-mapped idea. The server already scopes the
    // list to alive posts (drafts + scheduled/published autopilot), so there's
    // no client-side status gate left to apply.
    const ideas = projectInputs
      .map(p => projectToIdea(p, prev.get(p.id)))
      .filter((i): i is PostIdea => i !== null)

    const siblingsPatch = siblingsPatchFromProjects(projectInputs)
    useIdeaStore.setState(state => ({
      ideas,
      loading: false,
      siblingsByProject: { ...state.siblingsByProject, ...siblingsPatch },
    }))

    if (ideas.length >= POSTS_PER_TOPIC && !ideas.some(reelStillPending)) {
      // Defensive: if the Complete event was missed but a full batch
      // landed, drop the fallback poll so we don't keep ticking. A reel's
      // video lands minutes after its poster, so we keep polling while any
      // reel still has a poster but no playable videoUrl — otherwise we'd
      // strand it on the poster + loader state forever if its
      // CreativeContentUpdated event were dropped.
      ideaPoller.cancel()
    }
  } catch (err) {
    console.error('Failed to fetch ideas:', err)
    useIdeaStore.setState({ loading: false })
  } finally {
    fetchInflight = false
  }
}, 500)

/** Per-project preview-sibling state for the PostModal. */
export interface ProjectSiblingsState {
  /** Map of LivePlatform → sibling Creative (the primary platform is excluded — that's idea.primaryCreative). */
  byPlatform: Partial<Record<LivePlatform, Creative>>
  /** True while the previewSiblings call is in flight. */
  loading: boolean
}

/**
 * The terminal publish result for one creative, kept OUTSIDE the idea/sibling
 * entities so surfaces that render from a snapshot copy (the calendar passes
 * PostModal a `useState` copy of the idea, not a store reference) can overlay
 * the live result on top of their stale entity. `applyPublishResultToIdea`
 * writes it; PostModal merges it over the primary creative.
 */
export interface PublishResultOverlay {
  /** Set on success — the creative is live. */
  publishedAt?: string
  /** Fresh marker on failure; null on success (an explicit "cleared"). */
  publishFailure: Creative['publishFailure'] | null
  /**
   * Epoch ms of the server-side emit (event `emittedAt`, falling back to
   * client receipt time). A `CreativePublishStarted` older than this is a
   * late/reordered delivery and must not resurrect the publishing spinner.
   */
  terminalAt: number
}

/**
 * Options for `confirmIdea`. `scheduledAt` schedules the selected platforms;
 * `postNow` is the Post now flow and tells the server to CLEAR any existing
 * slot on them first (the manual publish endpoints refuse a post that still
 * carries a future slot; see the early-publish guard in
 * PublishLifecycleService.loadAndValidate).
 */
export interface ConfirmIdeaOptions {
  scheduledAt?: string
  postNow?: boolean
}

interface IdeaStore {
  ideas: PostIdea[]
  loading: boolean
  generating: boolean
  regenerating: boolean
  /** Cached per-platform sibling creatives spawned by previewSiblings. */
  siblingsByProject: Record<string, ProjectSiblingsState>
  /**
   * Creative IDs currently mid-publish (retry-now or an autopilot sweep in
   * flight). Driven by the Pusher `CreativePublishStarted`/`Complete` events so
   * an OPEN PostModal reflects the live publishing → live/failed transition
   * without a reopen.
   */
  publishingCreativeIds: string[]
  /** Latest terminal publish result per creative id — see PublishResultOverlay. */
  publishResultsByCreative: Record<string, PublishResultOverlay>

  fetchIdeas: () => void
  /**
   * Hydrate ideas directly from the topics endpoint payload (avoids a second
   * round-trip to /v1/projects?type=post on first load — the server already
   * shipped the suggested Posts grouped by topicId).
   */
  hydrateFromTopics: (postsByTopicId: Record<string, unknown[]>) => void
  /**
   * Fetch ONE Project by id, hydrate it into the ideas array, and return the
   * resulting PostIdea. Used by surfaces (Weekly Cockpit) that need to open
   * the PostModal for a Project that isn't on the home grid — e.g. autopilot-
   * generated posts (`source='autopilot'`) which are filtered out of the
   * home `findAll(kind='post')` query.
   */
  loadIdea: (projectId: string) => Promise<PostIdea | null>
  /**
   * Approve a review-mode post for publishing (stamps the Project's approvedAt
   * via the post's primary creative). Optimistically sets `approvedAt` on the
   * idea and keeps the Weekly Cockpit queue in sync. Throws on server failure.
   *
   * With `options.platforms` (+ `scheduledAt`) the approval carries the
   * user's selection: the server runs the confirm contract inside it, so every
   * selected platform (previews included) is scheduled into the slot and
   * `confirmedPlatforms` is written. This is the PostModal path; without
   * options it's the quick-approve used by the cockpit and calendar.
   */
  approve: (
    projectId: string,
    options?: { platforms?: LivePlatform[]; scheduledAt?: string }
  ) => Promise<void>
  /**
   * Unschedule a scheduled post (clears `scheduledAt` → back to a draft).
   * Optimistically flips the idea to `'draft'` and delegates the API call to the
   * Weekly Cockpit store so the cockpit queue stays in sync (mirrors `approve`).
   * The POST_UNSCHEDULED event is emitted server-side. Throws on failure.
   */
  unschedule: (projectId: string) => Promise<void>
  /**
   * Reflect an already-performed unschedule on the home grid — flips the owning
   * idea (matched by primary creative id) back to a `'draft'`. No-op when the
   * idea isn't loaded. Used by surfaces that drive the unschedule API through
   * another store (the Weekly Cockpit) but still need the IdeaCard's "Scheduled"
   * chip to clear without a refetch.
   */
  markUnscheduled: (creativeId: string) => void
  /** Filtered + ordered ideas for one topic. Convenience over external `selectIdeasForTopic`. */
  ideasForTopic: (topicId: string | null) => PostIdea[]
  setIdeaReady: (projectId: string) => void
  setGenerationComplete: () => void
  /**
   * Shallow-merge fields into the primary Creative's `content` for whichever
   * idea owns it. Used by the `creative:content:updated` Pusher handler so
   * the client can react to background partial updates (notably background
   * video gen completing for `short_vertical_video` IdeaCards) without a
   * full refetch.
   */
  patchCreativeContent: (creativeId: string, fields: Record<string, unknown>) => void
  /** Optimistic contentRevision bump after a local canonical edit. */
  noteCanonicalEditLocally: (projectId: string) => void
  /** Optimistic pin after a local user edit on a platform variant. */
  markSiblingPinnedLocally: (projectId: string, creativeId: string) => void
  /** Optimistic unpin after "Reset to match post" (rederive accepted). */
  markSiblingUnpinnedLocally: (projectId: string, creativeId: string, revision: number) => void
  removeIdea: (projectId: string) => void
  /**
   * Hard-delete a Post (Project) and all its sibling Creatives. Optimistically
   * removes from `ideas`; on server failure, re-hydrates the idea (rollback)
   * and re-throws so the caller can surface the error. Also kicks the Weekly
   * Cockpit feed so the deleted post disappears from the cockpit immediately.
   */
  deletePost: (projectId: string) => Promise<void>
  /**
   * Confirm a Post on the server (spawns siblings, stamps scheduledAt).
   * Default behaviour removes the idea from the home grid (legacy + schedule
   * mode). Pass `keepInList: true` from the new "Post now" flow so the idea
   * stays on the grid in `'publishing'` state while PostPublishProgressDialog
   * runs the per-platform publishes.
   */
  /**
   * Throws if the server rejects (e.g. project status doesn't allow
   * confirm/reschedule, network error, validation). The single caller
   * (PostModal) catches and surfaces an inline error. The error-reporting capture
   * inside this action is for telemetry; the throw is the contract.
   */
  confirmIdea: (
    projectId: string,
    platforms: LivePlatform[],
    options?: ConfirmIdeaOptions,
    flow?: { keepInList?: boolean }
  ) => Promise<string>
  /**
   * Merge a single platform's publish result into the idea. Once every
   * targeted platform reaches a terminal state (`success` | `failed`),
   * `idea.status` flips to `'live'` and `publishedAt` is stamped.
   */
  setIdeaPlatformResult: (
    projectId: string,
    platform: LivePlatform,
    result: IdeaPlatformResult
  ) => void
  /**
   * Reset failed platform entries back to `pending` so PostModal can re-fan
   * them out. Returns the list of platforms that were failed (to seed the
   * modal selection).
   */
  republishIdeaFailures: (projectId: string) => LivePlatform[]
  discardIdea: (projectId: string) => Promise<void>
  regenerateAll: () => Promise<void>
  /**
   * Regenerate the primary creative for one Post — replaces the previous one.
   * `opts.userDirection` (optional) is a free-form string the user typed in
   * the IdeaCard Edit popover; the server threads it into the agent prompt
   * so both copy and image are steered.
   */
  regenerateIdea: (projectId: string, opts?: { userDirection?: string }) => Promise<void>
  /**
   * Derive draft sibling creatives so the composer/PostModal render
   * platform-faithful previews. The single idempotent entry point for
   * sibling derivation: skips cached rows with usable media, skips
   * platforms already in flight (across ALL calling surfaces), and the
   * server dedupes rows by (projectId, type). Safe to call repeatedly with
   * the full wanted set. Streams results into siblingsByProject.
   */
  previewSiblings: (projectId: string, platforms: LivePlatform[]) => Promise<void>
  /**
   * Flag/unflag a creative as mid-publish (adds/removes from
   * `publishingCreativeIds`). Called optimistically on retry-now and from the
   * Pusher `CreativePublishStarted` handler. `emittedAt` (epoch ms, from the
   * event payload) guards against reordered delivery: the server fires
   * `started` without awaiting it, so on a near-instant failure the `complete`
   * event can be RECEIVED by Pusher first — a `started` older than the
   * creative's last terminal result is dropped instead of resurrecting the
   * spinner forever. Calls without `emittedAt` (the optimistic retry tap)
   * always apply.
   */
  markCreativePublishing: (creativeId: string, publishing: boolean, emittedAt?: number) => void
  /**
   * Apply a terminal publish result to the matching creative wherever it lives
   * (an idea's primary + any harvested sibling), so an OPEN PostModal updates in
   * place: success → stamp `publishedAt` + clear `publishFailure`; failure →
   * (re)stamp `publishFailure` with the fresh reason. Always clears the creative
   * from `publishingCreativeIds`, and records the result in
   * `publishResultsByCreative` for snapshot-rendering surfaces.
   */
  applyPublishResultToIdea: (
    creativeId: string,
    result: { success: boolean; failureReason?: string; error?: string; emittedAt?: number }
  ) => void
}

// Platforms with a preview-siblings request currently in flight, per project.
// Module-scoped (not store state) so the filter is synchronous within a React
// commit: two surfaces firing in the same tick can't double-request a
// platform — the store-state `loading` flag is only written asynchronously
// and effects read stale render snapshots of it.
const siblingsInFlight = new Map<string, Set<LivePlatform>>()

export const useIdeaStore = create<IdeaStore>(() => ({
  ideas: [],
  // start in loading state so the home grid renders skeletons immediately on
  // first mount (rather than an empty section while the debounce + initial
  // fetch round-trip resolves).
  loading: true,
  generating: false,
  regenerating: false,
  siblingsByProject: {},
  publishingCreativeIds: [],
  publishResultsByCreative: {},

  fetchIdeas: () => {
    debouncedFetchImpl()
  },

  hydrateFromTopics: (postsByTopicId: Record<string, unknown[]>) => {
    const incoming: PostIdea[] = []
    const projectInputs: Array<{
      id: string
      data?: Record<string, unknown> | null
      creatives?: Parameters<typeof entityToCreative>[0][]
      requiresApproval?: boolean
      approvedAt?: string | null
    }> = []
    const prev = new Map(
      useIdeaStore.getState().ideas.map(i => [
        i.projectId,
        {
          isRegenerating: i.isRegenerating,
          status: i.status,
          publishedAt: i.publishedAt,
          scheduledAt: i.scheduledAt,
          platformResults: i.platformResults,
        },
      ])
    )
    for (const posts of Object.values(postsByTopicId)) {
      for (const raw of posts) {
        const p = raw as {
          id: string
          data?: Record<string, unknown> | null
          creatives?: Parameters<typeof entityToCreative>[0][]
          requiresApproval?: boolean
          approvedAt?: string | null
        }
        projectInputs.push(p)
        const idea = projectToIdea(p, prev.get(p.id))
        // Keep every successfully-mapped idea — the server already scopes the
        // payload to alive posts.
        if (idea) incoming.push(idea)
      }
    }
    const siblingsPatch = siblingsPatchFromProjects(projectInputs)
    // Merge: preserve optimistic isRegenerating flag and any local
    // publishing/live state from a PostModal flow.
    useIdeaStore.setState(state => {
      const prev = new Map(state.ideas.map(i => [i.projectId, i]))
      const merged = incoming.map(i => {
        const existing = prev.get(i.projectId)
        if (!existing) return i
        const next: PostIdea = { ...i }
        if (existing.isRegenerating) next.isRegenerating = true
        if (
          existing.status === 'publishing' ||
          existing.status === 'live' ||
          existing.status === 'scheduled'
        ) {
          next.status = existing.status
          next.publishedAt = existing.publishedAt
          next.scheduledAt = existing.scheduledAt
          next.platformResults = existing.platformResults
        }
        return next
      })
      // The topics payload can NEVER contain topic-less posts (intent drafts,
      // autopilot slots) — a wholesale replace would wipe them from the store
      // on every fetchTopics, blanking Home's resume strip until the next
      // fetchIdeas round-trip. Preserve them across the hydrate.
      const topicless = state.ideas.filter(i => i.topicId === null)
      return {
        ideas: [...merged, ...topicless],
        loading: false,
        siblingsByProject: { ...state.siblingsByProject, ...siblingsPatch },
      }
    })
  },

  ideasForTopic: (topicId: string | null): PostIdea[] => {
    const ideas = useIdeaStore.getState().ideas
    return selectIdeasForTopic(ideas, topicId)
  },

  loadIdea: async (projectId: string): Promise<PostIdea | null> => {
    try {
      const response = await apiCall(ProjectService.projectControllerFindOneV1, null, projectId)
      if (!response) return null
      const project = response.project as {
        id: string
        data?: Record<string, unknown> | null
        requiresApproval?: boolean
        approvedAt?: string | null
      }
      const creatives = (response.creatives ?? []) as Parameters<typeof entityToCreative>[0][]
      // Preserve any local transient state (publishing/live/scheduled) the
      // store already had for this projectId so a second open doesn't clobber
      // an in-flight publish progress.
      const prev = useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
      const idea = projectToIdea(
        {
          id: project.id,
          data: project.data,
          creatives,
          requiresApproval: project.requiresApproval,
          approvedAt: project.approvedAt,
        },
        prev
          ? {
              isRegenerating: prev.isRegenerating,
              status: prev.status,
              publishedAt: prev.publishedAt,
              scheduledAt: prev.scheduledAt,
              platformResults: prev.platformResults,
            }
          : undefined
      )
      if (!idea) return null

      // Merge into ideas array so PostModal's reactive selectors (siblings,
      // regenerating flag, status updates from Pusher) flow through the
      // store. Replaces any existing entry with the same projectId.
      const siblingsPatch = siblingsPatchFromProjects([
        { id: project.id, data: project.data, creatives },
      ])
      useIdeaStore.setState(state => {
        const without = state.ideas.filter(i => i.projectId !== projectId)
        return {
          ideas: [...without, idea],
          siblingsByProject: { ...state.siblingsByProject, ...siblingsPatch },
        }
      })

      // Mirror primary + siblings into the creative store so the composer's
      // per-platform editors (which read from useCreativeStore by id) find
      // them on first paint without an extra fetch.
      const allCreatives = creatives.map(entityToCreative)
      useCreativeStore.setState(state => {
        const existing = new Map(state.creatives.map(c => [c.id, c]))
        for (const c of allCreatives) existing.set(c.id, c)
        return { creatives: Array.from(existing.values()) }
      })

      return idea
    } catch (err) {
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'idea_load_one',
        projectId,
      })
      console.error('Failed to load idea:', err)
      return null
    }
  },

  approve: async (
    projectId: string,
    options?: { platforms?: LivePlatform[]; scheduledAt?: string }
  ) => {
    const state = useIdeaStore.getState()
    const idea = state.ideas.find(i => i.projectId === projectId)
    const creativeId = idea?.primaryCreative?.id
    if (!creativeId) {
      throw new Error('Cannot approve: post has no primary creative')
    }
    const platforms =
      options?.platforms && options.platforms.length > 0 ? options.platforms : undefined
    if (platforms) {
      // Same preamble as confirmIdea: land any pending optimistic edits before
      // the server re-reads the primary + siblings inside the confirm contract.
      const pendingIds = [
        creativeId,
        ...Object.values(state.siblingsByProject[projectId]?.byPlatform ?? {}).map(c => c?.id),
      ].filter((id): id is string => !!id)
      await flushPersistsAndWait(pendingIds)
    }
    // Delegate to the Weekly Cockpit store: it owns the approve API call plus the
    // optimistic queue update + rollback, so the cockpit stays in sync when the
    // composer was opened from it. Re-throws on server failure. Safe when the
    // queue isn't loaded (the optimistic step is a no-op; the API still fires).
    await useWeeklyQueueStore
      .getState()
      .approvePost(
        creativeId,
        platforms ? { platforms, scheduledAt: options?.scheduledAt } : undefined
      )
    // Reflect approval on the idea so the composer CTA flips to "Scheduled".
    // A selection-carrying approve also mirrors the committed platforms + slot
    // (the server's confirmedPlatforms = the request), so reopening PostModal
    // in the same session seeds from what will actually publish.
    useIdeaStore.setState(s => ({
      ideas: s.ideas.map(i =>
        i.projectId === projectId
          ? {
              ...i,
              approvedAt: new Date().toISOString(),
              ...(platforms && { confirmedPlatforms: platforms }),
              ...(options?.scheduledAt && {
                status: 'scheduled' as const,
                scheduledAt: options.scheduledAt,
                primaryCreative: i.primaryCreative
                  ? ({
                      ...i.primaryCreative,
                      scheduledAt: options.scheduledAt,
                    } as typeof i.primaryCreative)
                  : i.primaryCreative,
              }),
            }
          : i
      ),
      // The server prunes unselected candidate previews; drop them from the
      // sibling cache so a reopen re-derives (idempotent) instead of trusting
      // a row that may no longer exist.
      ...(platforms && s.siblingsByProject[projectId]
        ? {
            siblingsByProject: {
              ...s.siblingsByProject,
              [projectId]: {
                ...s.siblingsByProject[projectId],
                byPlatform: Object.fromEntries(
                  Object.entries(s.siblingsByProject[projectId].byPlatform).filter(
                    ([platform, c]) =>
                      platforms.includes(platform as LivePlatform) ||
                      !!c?.publishedAt ||
                      !!c?.scheduledAt
                  )
                ) as Partial<Record<LivePlatform, Creative>>,
              },
            },
          }
        : null),
    }))
    // A selection-carrying approve changes rows server-side (previews flipped
    // into the slot, the primary possibly re-pointed): fold the real rows the
    // way confirmIdea does, so a later strip removal or unschedule acts on a
    // sibling's true scheduledAt instead of the stale preview cache.
    if (platforms) {
      await useIdeaStore
        .getState()
        .loadIdea(projectId)
        .catch(() => null)
    }
  },

  unschedule: async (projectId: string) => {
    const idea = useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
    const creativeId = idea?.primaryCreative?.id
    if (!creativeId) {
      throw new Error('Cannot unschedule: post has no primary creative')
    }
    // Delegate to the Weekly Cockpit store: it owns the unschedule API call plus
    // the optimistic queue update + rollback, so the cockpit stays in sync when
    // the modal was opened from it. Re-throws on server failure.
    await useWeeklyQueueStore.getState().unschedule(creativeId)
    // Reflect on the idea so the home grid flips the card back to a draft.
    useIdeaStore.getState().markUnscheduled(creativeId)
  },

  markUnscheduled: (creativeId: string) => {
    useIdeaStore.setState(state => ({
      ideas: state.ideas.map(i =>
        i.primaryCreative?.id === creativeId
          ? {
              ...i,
              status: 'draft' as const,
              scheduledAt: undefined,
              approvedAt: null,
              // The composer reads the active creative straight off the idea
              // (ComposerPage passes `idea.primaryCreative`), and its Schedule
              // CTA/pill falls back to `creative.scheduledAt`. Clear it here too,
              // otherwise the "Scheduled for …" pill survives the unschedule.
              primaryCreative: i.primaryCreative
                ? ({ ...i.primaryCreative, scheduledAt: null } as typeof i.primaryCreative)
                : i.primaryCreative,
            }
          : i
      ),
    }))
  },

  setIdeaReady: (projectId: string) => {
    // Clear isRegenerating eagerly for the specific project that just became
    // ready. The refetch below will pick up the new primary creative content;
    // we don't want to wait for the round-trip to swap the shimmer overlay
    // off.
    useIdeaStore.setState(state => ({
      ideas: state.ideas.map(i =>
        i.projectId === projectId && i.isRegenerating ? { ...i, isRegenerating: false } : i
      ),
    }))
    // Topic-less posts (creation intents, autopilot) are invisible to the
    // topics refetch — GET /v1/topics only returns posts under a topic — so
    // refresh them directly INSTEAD of the (heavy) topics fetch, which by
    // construction cannot contain them. Topic posts keep the debounced topics
    // refetch. An idea not in the store yet has no surface watching it; the
    // arrival view's own loadIdea + safety poll cover that races.
    const idea = useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
    if (idea && !idea.topicId) {
      void useIdeaStore
        .getState()
        .loadIdea(projectId)
        .catch(err => console.error('loadIdea after ready failed:', err))
    } else {
      debouncedFetchImpl()
    }
  },

  setGenerationComplete: () => {
    // "Complete" means the agent batch is done — but a reel's video can still
    // be rendering in the background. Keep the fallback poll alive while any
    // reel has a poster but no playable videoUrl yet.
    if (!useIdeaStore.getState().ideas.some(reelStillPending)) {
      ideaPoller.cancel()
    }
    useIdeaStore.setState({ generating: false, regenerating: false })
    debouncedFetchImpl.flush()
  },

  noteCanonicalEditLocally: (projectId: string) => {
    // Optimistic mirror of the server-side contentRevision bump so the
    // "behind the post" badge math is fresh without a refetch.
    useIdeaStore.setState(state => ({
      ideas: state.ideas.map(i =>
        i.projectId === projectId ? { ...i, contentRevision: (i.contentRevision ?? 0) + 1 } : i
      ),
    }))
  },

  markSiblingPinnedLocally: (projectId: string, creativeId: string) => {
    // Optimistic mirror of the server-side pin (a user content edit on a
    // variant) so the "Customized" badge appears immediately.
    useIdeaStore.setState(state => {
      const project = state.siblingsByProject[projectId]
      if (!project) return state
      let mutated = false
      const byPlatform: ProjectSiblingsState['byPlatform'] = { ...project.byPlatform }
      for (const [platform, sibling] of Object.entries(project.byPlatform)) {
        if (sibling?.id !== creativeId || sibling.pinnedAt) continue
        byPlatform[platform as LivePlatform] = { ...sibling, pinnedAt: new Date().toISOString() }
        mutated = true
      }
      if (!mutated) return state
      return {
        siblingsByProject: {
          ...state.siblingsByProject,
          [projectId]: { ...project, byPlatform },
        },
      }
    })
  },

  markSiblingUnpinnedLocally: (projectId: string, creativeId: string, revision: number) => {
    useIdeaStore.setState(state => {
      const project = state.siblingsByProject[projectId]
      if (!project) return state
      let mutated = false
      const byPlatform: ProjectSiblingsState['byPlatform'] = { ...project.byPlatform }
      for (const [platform, sibling] of Object.entries(project.byPlatform)) {
        if (sibling?.id !== creativeId || !sibling.pinnedAt) continue
        byPlatform[platform as LivePlatform] = {
          ...sibling,
          pinnedAt: null,
          derivedFromRevision: revision,
        }
        mutated = true
      }
      if (!mutated) return state
      return {
        siblingsByProject: {
          ...state.siblingsByProject,
          [projectId]: { ...project, byPlatform },
        },
      }
    })
  },

  patchCreativeContent: (creativeId: string, fields: Record<string, unknown>) => {
    useIdeaStore.setState(state => {
      // Patch primary creative on any matching idea.
      const ideas = state.ideas.map(idea => {
        if (idea.primaryCreative?.id !== creativeId) return idea
        return {
          ...idea,
          primaryCreative: {
            ...idea.primaryCreative,
            content: {
              ...(idea.primaryCreative.content as unknown as Record<string, unknown>),
              ...fields,
            },
          } as unknown as Creative,
        }
      })

      // Also patch any preview sibling — sibling background generation fires
      // CreativeContentUpdated events the same way the primary does, so the
      // PostModal preview pane needs them to land in byPlatform[platform]
      // (not just on idea.primaryCreative).
      let siblingsByProject = state.siblingsByProject
      for (const [projectId, projectState] of Object.entries(state.siblingsByProject)) {
        let mutated = false
        const nextByPlatform: ProjectSiblingsState['byPlatform'] = {
          ...projectState.byPlatform,
        }
        for (const [platform, sibling] of Object.entries(projectState.byPlatform)) {
          if (sibling?.id !== creativeId) continue
          nextByPlatform[platform as LivePlatform] = {
            ...sibling,
            content: {
              ...(sibling.content as unknown as Record<string, unknown>),
              ...fields,
            },
          } as unknown as Creative
          mutated = true
        }
        if (mutated) {
          if (siblingsByProject === state.siblingsByProject) {
            siblingsByProject = { ...state.siblingsByProject }
          }
          siblingsByProject[projectId] = {
            ...projectState,
            byPlatform: nextByPlatform,
          }
        }
      }

      return { ideas, siblingsByProject }
    })
  },

  markCreativePublishing: (creativeId: string, publishing: boolean, emittedAt?: number) => {
    useIdeaStore.setState(state => {
      // Late-delivery guard: a `started` event that predates the creative's
      // last terminal result must not re-add the id — nothing would ever
      // remove it again (the matching `complete` already ran).
      if (publishing && emittedAt !== undefined) {
        const terminal = state.publishResultsByCreative[creativeId]
        if (terminal && emittedAt <= terminal.terminalAt) return state
      }
      const has = state.publishingCreativeIds.includes(creativeId)
      if (publishing === has) return state
      return {
        publishingCreativeIds: publishing
          ? [...state.publishingCreativeIds, creativeId]
          : state.publishingCreativeIds.filter(id => id !== creativeId),
      }
    })
  },

  applyPublishResultToIdea: (creativeId, result) => {
    useIdeaStore.setState(state => {
      const terminalAt = result.emittedAt ?? Date.now()
      const now = new Date(terminalAt).toISOString()
      // Patch a Creative in place: success → live (publishedAt set, marker
      // cleared); failure → (re)stamp the marker with the fresh reason.
      const patch = (c: Creative | null | undefined): Creative | null | undefined => {
        if (!c || c.id !== creativeId) return c
        if (result.success) {
          return { ...c, publishedAt: now, publishFailure: undefined }
        }
        return {
          ...c,
          publishFailure: {
            error: result.error ?? '',
            reason: result.failureReason ?? 'unknown',
            failedAt: now,
          },
        }
      }

      const ideas = state.ideas.map(idea =>
        idea.primaryCreative?.id === creativeId
          ? { ...idea, primaryCreative: patch(idea.primaryCreative) ?? null }
          : idea
      )

      let siblingsByProject = state.siblingsByProject
      for (const [projectId, projectState] of Object.entries(state.siblingsByProject)) {
        let mutated = false
        const nextByPlatform: ProjectSiblingsState['byPlatform'] = { ...projectState.byPlatform }
        for (const [platform, sibling] of Object.entries(projectState.byPlatform)) {
          if (sibling?.id !== creativeId) continue
          nextByPlatform[platform as LivePlatform] = patch(sibling) ?? undefined
          mutated = true
        }
        if (mutated) {
          if (siblingsByProject === state.siblingsByProject) {
            siblingsByProject = { ...state.siblingsByProject }
          }
          siblingsByProject[projectId] = { ...projectState, byPlatform: nextByPlatform }
        }
      }

      return {
        ideas,
        siblingsByProject,
        publishingCreativeIds: state.publishingCreativeIds.filter(id => id !== creativeId),
        // Overlay for snapshot-rendering surfaces (calendar-opened PostModal):
        // they merge this over their stale entity copy instead of missing the
        // store patches above entirely.
        publishResultsByCreative: {
          ...state.publishResultsByCreative,
          [creativeId]: result.success
            ? { publishedAt: now, publishFailure: null, terminalAt }
            : {
                publishFailure: {
                  error: result.error ?? '',
                  reason: result.failureReason ?? 'unknown',
                  failedAt: now,
                },
                terminalAt,
              },
        },
      }
    })
  },

  removeIdea: (projectId: string) => {
    useIdeaStore.setState(state => ({
      ideas: state.ideas.filter(i => i.projectId !== projectId),
    }))
  },

  confirmIdea: async (
    projectId: string,
    platforms: LivePlatform[],
    options?: ConfirmIdeaOptions,
    flow?: { keepInList?: boolean }
  ) => {
    try {
      // Land any pending optimistic edits BEFORE confirming — the response
      // fold below overwrites both stores with server rows.
      const state = useIdeaStore.getState()
      const pendingIds = [
        state.ideas.find(i => i.projectId === projectId)?.primaryCreative?.id,
        ...Object.values(state.siblingsByProject[projectId]?.byPlatform ?? {}).map(c => c?.id),
      ].filter((id): id is string => !!id)
      await flushPersistsAndWait(pendingIds)
      const response = await apiCall(
        ProjectService.projectControllerConfirmPostV1,
        null,
        projectId,
        {
          platforms,
          ...(options?.scheduledAt && { scheduledAt: options.scheduledAt }),
          ...(options?.postNow && { postNow: true }),
        }
      )
      // Move all returned creatives into the creative store so the Post page
      // and composer can find them locally.
      const creatives = response.creatives.map(entityToCreative)
      useCreativeStore.setState(state => {
        const existing = new Map(state.creatives.map(c => [c.id, c]))
        for (const c of creatives) existing.set(c.id, c)
        return { creatives: Array.from(existing.values()) }
      })
      // Fold the non-primary creatives into the sibling cache too — confirm
      // spawns any missing sibling server-side, and post-confirm surfaces
      // (composer strip, retry-failures modal) read siblingsByProject; without
      // this they'd refetch rows the client just received.
      useIdeaStore.setState(state => {
        const primaryPlatform = state.ideas.find(i => i.projectId === projectId)?.primaryPlatform
        const byPlatform: Partial<Record<LivePlatform, Creative>> = {
          ...(state.siblingsByProject[projectId]?.byPlatform ?? {}),
        }
        for (const c of creatives) {
          const p = creativeTypeToPlatform(c.type)
          if (p && p !== primaryPlatform) byPlatform[p] = c
        }
        return {
          siblingsByProject: {
            ...state.siblingsByProject,
            [projectId]: {
              byPlatform,
              loading: state.siblingsByProject[projectId]?.loading ?? false,
            },
          },
        }
      })
      if (options?.scheduledAt) {
        // Schedule flow — leave the card on the grid in 'scheduled' state so
        // the user sees instant feedback. The pill on IdeaCard renders the
        // scheduledAt; tapping it reopens PostModal in schedule mode.
        const scheduledAt = options.scheduledAt
        // The server's primary creative carries the authoritative
        // scheduledAt + autopilotId; mirror it onto `idea.primaryCreative`
        // so surfaces that read from the idea-store snapshot (e.g. the
        // composer's Schedule button — `idea.primaryCreative.scheduledAt`)
        // flip immediately. Without this the creative-store update above
        // lands but the idea-store's stale `primaryCreative` keeps the
        // button stuck on "Schedule".
        const updatedPrimaryById = new Map(creatives.map(c => [c.id, c]))
        useIdeaStore.setState(state => ({
          ideas: state.ideas.map(i => {
            if (i.projectId !== projectId) return i
            const refreshedPrimary =
              i.primaryCreative && updatedPrimaryById.has(i.primaryCreative.id)
                ? updatedPrimaryById.get(i.primaryCreative.id)!
                : i.primaryCreative
            return {
              ...i,
              status: 'scheduled',
              scheduledAt,
              publishedAt: undefined,
              platformResults: undefined,
              primaryCreative: refreshedPrimary,
              // Honor the just-confirmed selection so reopening PostModal in the
              // same session seeds from the real platforms (not a stale set /
              // [primaryPlatform]). Mirrors the server's confirmedPlatforms =
              // requestedAll; without this a dropped primary gets silently
              // re-checked on the next confirm.
              confirmedPlatforms: platforms,
            }
          }),
        }))
        // Refresh the cockpit feed so the new scheduled post shows up on
        // its day immediately — confirmPost server-side stamps the active
        // autopilotId on the primary + siblings, which makes them eligible
        // for the cockpit's `autopilotId IS NOT NULL` query. Without this
        // refetch the user has to reload the page to see the card appear.
        // Fire-and-forget; failures bubble through fetchQueue's own error-reporting
        // capture.
        void useWeeklyQueueStore.getState().fetchQueue()
      } else if (flow?.keepInList) {
        // "Post now" flow — leave the card on the grid in 'publishing' state.
        // PostPublishProgressDialog will fan out per-platform publishes and
        // call setIdeaPlatformResult as each one terminates.
        //
        // Resolve creativeIds from the confirm RESPONSE first — the server
        // spawns any missing sibling during confirm, and under lazy sibling
        // derivation the client cache may not have it yet (select → Post now
        // inside one round-trip). The cache + primary remain the fallback.
        const byPlatformFromResponse: Partial<Record<LivePlatform, string>> = {}
        for (const c of creatives) {
          const p = creativeTypeToPlatform(c.type)
          if (p) byPlatformFromResponse[p] = c.id
        }
        useIdeaStore.setState(state => ({
          ideas: state.ideas.map(i => {
            if (i.projectId !== projectId) return i
            const seeded: PostIdea['platformResults'] = {}
            const siblings = state.siblingsByProject[projectId]?.byPlatform ?? {}
            for (const p of platforms) {
              // Primary stays pinned to the known primary creative — a
              // duplicate/mis-typed row in the response mapping to the
              // primary's platform must not hijack what gets published there.
              const creativeId =
                p === i.primaryPlatform
                  ? (i.primaryCreative?.id ?? byPlatformFromResponse[p] ?? '')
                  : (byPlatformFromResponse[p] ?? siblings[p]?.id ?? '')
              seeded[p] = { creativeId, state: 'pending' }
            }
            // Post now cleared the schedule server-side; drop the stale slot so no
            // surface keeps reading "Scheduled for ..." on a post that is going live.
            return {
              ...i,
              status: 'publishing',
              platformResults: seeded,
              scheduledAt: undefined,
              // The composer header reads the slot off the primary creative as
              // a fallback; clear it too or "Scheduled for …" outlives the publish.
              primaryCreative: i.primaryCreative
                ? ({ ...i.primaryCreative, scheduledAt: null } as typeof i.primaryCreative)
                : i.primaryCreative,
            }
          }),
        }))
      } else {
        // Legacy flow — remove the idea from the home page list.
        useIdeaStore.setState(state => ({
          ideas: state.ideas.filter(i => i.projectId !== projectId),
        }))
      }
      return response.project.id
    } catch (err) {
      // Telemetry for the surface that triggered the confirm — keep this
      // even though we re-throw so the failure ends up tagged with
      // step/projectId/platformCount context, not just whatever boundary
      // the throw lands at.
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'post_modal',
        step: options?.scheduledAt ? 'schedule' : 'confirm',
        projectId,
        platformCount: platforms.length,
      })
      // Propagate so PostModal can detect the failure and surface an inline
      // error instead of celebrating a no-op. (Was previously returning
      // null, which made PostModal's try/catch dead code and the success
      // animation play on every failure.)
      throw err
    }
  },

  setIdeaPlatformResult: (
    projectId: string,
    platform: LivePlatform,
    result: IdeaPlatformResult
  ) => {
    useIdeaStore.setState(state => ({
      ideas: state.ideas.map(i => {
        if (i.projectId !== projectId) return i
        const merged: PostIdea['platformResults'] = {
          ...(i.platformResults ?? {}),
          [platform]: result,
        }
        // Aggregate: terminal once every entry is success|failed (no pendings).
        const entries = Object.values(merged)
        const allTerminal = entries.every(r => r?.state === 'success' || r?.state === 'failed')
        const anySuccess = entries.some(r => r?.state === 'success')
        // Live only when something actually went out. A post-now whose every
        // platform failed is an unscheduled draft with failure chips (its slot
        // was cleared before the publishes fired), never a fake "live".
        return {
          ...i,
          platformResults: merged,
          ...(allTerminal
            ? anySuccess
              ? { status: 'live' as const, publishedAt: new Date().toISOString() }
              : { status: 'draft' as const, publishedAt: undefined }
            : {}),
        }
      }),
    }))
  },

  republishIdeaFailures: (projectId: string) => {
    const idea = useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
    if (!idea?.platformResults) return []
    const failed: LivePlatform[] = []
    const next: PostIdea['platformResults'] = { ...idea.platformResults }
    for (const [platform, r] of Object.entries(idea.platformResults)) {
      if (r?.state === 'failed') {
        failed.push(platform as LivePlatform)
        next[platform as LivePlatform] = { ...r, state: 'pending', error: undefined }
      }
    }
    if (failed.length === 0) return []
    useIdeaStore.setState(state => ({
      ideas: state.ideas.map(i =>
        i.projectId === projectId
          ? { ...i, status: 'publishing', platformResults: next, publishedAt: undefined }
          : i
      ),
    }))
    return failed
  },

  discardIdea: async (projectId: string) => {
    useIdeaStore.setState(state => ({
      ideas: state.ideas.filter(i => i.projectId !== projectId),
    }))
    try {
      await apiCall(ProjectService.projectControllerDiscardPostV1, null, projectId)
    } catch (err) {
      console.error('Failed to discard Post:', err)
      debouncedFetchImpl()
    }
  },

  deletePost: async (projectId: string) => {
    // Snapshot the idea + its creative IDs so we can roll back on failure.
    // The server hard-deletes the Project; the FK cascade
    // (FK_Creatives_projectId_Projects ON DELETE CASCADE, migration
    // 1779300000000) takes the sibling Creatives with it, so we mirror that
    // locally by stripping every Creative whose projectId matches.
    const prev = useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
    const prevCreatives = useCreativeStore.getState().creatives.filter(c => {
      const pid = (c as { projectId?: string | null }).projectId
      return pid === projectId
    })
    useIdeaStore.setState(state => ({
      ideas: state.ideas.filter(i => i.projectId !== projectId),
    }))
    if (prevCreatives.length > 0) {
      const prevIds = new Set(prevCreatives.map(c => c.id))
      useCreativeStore.setState(state => ({
        creatives: state.creatives.filter(c => !prevIds.has(c.id)),
      }))
    }
    try {
      await apiCall(ProjectService.projectControllerDeleteV1, null, projectId)
      // Refresh the cockpit so the deleted post drops out immediately. The
      // cockpit lives in its own Zustand store; without this it keeps the
      // stale card around until the next manual fetch (or page reload).
      void useWeeklyQueueStore.getState().fetchQueue()
    } catch (err) {
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'post_delete',
        projectId,
      })
      // Roll back the optimistic removals so the UI matches server state.
      if (prev) {
        useIdeaStore.setState(state => ({
          ideas: state.ideas.some(i => i.projectId === projectId)
            ? state.ideas
            : [...state.ideas, prev],
        }))
      }
      if (prevCreatives.length > 0) {
        useCreativeStore.setState(state => {
          const existing = new Map(state.creatives.map(c => [c.id, c]))
          for (const c of prevCreatives) if (!existing.has(c.id)) existing.set(c.id, c)
          return { creatives: Array.from(existing.values()) }
        })
      }
      throw err
    }
  },

  regenerateAll: async () => {
    if (useIdeaStore.getState().regenerating) return
    useIdeaStore.setState({ ideas: [], regenerating: true, generating: true })
    try {
      await apiCall(CreativeService.creativeControllerRegenerateAllIdeasV1, null)
      ideaPoller.schedule(() => debouncedFetchImpl())
    } catch (err) {
      console.error('Failed to regenerate ideas:', err)
      useIdeaStore.setState({ regenerating: false, generating: false })
      debouncedFetchImpl()
    }
  },

  regenerateIdea: async (projectId: string, opts?: { userDirection?: string }) => {
    const current = useIdeaStore.getState().ideas.find(i => i.projectId === projectId)
    if (!current || current.isRegenerating) return

    // Optimistic flag — IdeaCard renders a spinner on the regen button until
    // the new variant lands via Pusher → fetch.
    useIdeaStore.setState(state => ({
      ideas: state.ideas.map(i => (i.projectId === projectId ? { ...i, isRegenerating: true } : i)),
    }))

    try {
      const direction = opts?.userDirection?.trim()
      await apiCall(
        ProjectService.projectControllerRegenerateCreativeV1,
        null,
        projectId,
        direction ? { userDirection: direction } : {}
      )
      // Schedule a backoff poll in case the Pusher event drops. Same pattern
      // as initial generation.
      ideaPoller.schedule(() => debouncedFetchImpl())
    } catch (err) {
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'idea_regenerate',
        projectId,
        shape: current.shape,
      })
      console.error('Failed to regenerate creative for Post:', err)
      useIdeaStore.setState(state => ({
        ideas: state.ideas.map(i =>
          i.projectId === projectId ? { ...i, isRegenerating: false } : i
        ),
      }))
    }
  },

  previewSiblings: async (projectId: string, platforms: LivePlatform[]) => {
    if (platforms.length === 0) return
    const inFlight = siblingsInFlight.get(projectId) ?? new Set<LivePlatform>()
    siblingsInFlight.set(projectId, inFlight)

    // Skip platforms already being requested, and platforms whose sibling is
    // already cached with a usable asset (pre-populated by hydrateFromTopics /
    // debouncedFetchImpl from the list payload, or by an earlier
    // previewSiblings + Pusher patches). Hitting the server for these only to
    // overwrite the cache with the same data creates a visible
    // request-in-flight blip on every modal open. Media-less cached rows ARE
    // re-requested — that's the self-heal path for rows hydrated
    // mid-generation whose content patch never arrived.
    const cached = useIdeaStore.getState().siblingsByProject[projectId]?.byPlatform ?? {}
    const missing = platforms.filter(p => {
      if (inFlight.has(p)) return false
      const c = cached[p]
      if (!c) return true
      const content = c.content as unknown as Record<string, unknown> | null | undefined
      if (!content) return true
      if (content.imageUrl || content.videoUrl) return false
      const items = content.mediaItems as unknown[] | undefined
      return !(Array.isArray(items) && items.length > 0)
    })
    if (missing.length === 0) return

    for (const p of missing) inFlight.add(p)
    useIdeaStore.setState(state => ({
      siblingsByProject: {
        ...state.siblingsByProject,
        [projectId]: {
          byPlatform: state.siblingsByProject[projectId]?.byPlatform ?? {},
          loading: true,
        },
      },
    }))

    try {
      const response = await apiCall(
        ProjectService.projectControllerPreviewSiblingsV1,
        null,
        projectId,
        { platforms: missing }
      )

      const byPlatform: Partial<Record<LivePlatform, Creative>> = {
        ...(useIdeaStore.getState().siblingsByProject[projectId]?.byPlatform ?? {}),
      }
      const incomingCreatives: Creative[] = []
      for (const entity of response.siblings) {
        const creative = entityToCreative(entity)
        const platform = creativeTypeToPlatform(creative.type)
        if (platform) byPlatform[platform] = creative
        incomingCreatives.push(creative)
      }

      useIdeaStore.setState(state => ({
        siblingsByProject: {
          ...state.siblingsByProject,
          [projectId]: { byPlatform, loading: true },
        },
      }))

      // Mirror into creative-store so other surfaces (composer, project page)
      // can pick them up by id without a separate fetch.
      useCreativeStore.setState(state => {
        const existing = new Map(state.creatives.map(c => [c.id, c]))
        for (const c of incomingCreatives) existing.set(c.id, c)
        return { creatives: Array.from(existing.values()) }
      })
    } catch (err) {
      console.error('Failed to load preview siblings:', err)
    } finally {
      // Release the in-flight platforms and derive `loading` from what's
      // still pending — a fast call completing must not flip the flag off
      // while a slower concurrent call for the same project is mid-flight.
      for (const p of missing) inFlight.delete(p)
      const stillLoading = inFlight.size > 0
      useIdeaStore.setState(state => ({
        siblingsByProject: {
          ...state.siblingsByProject,
          [projectId]: {
            byPlatform: state.siblingsByProject[projectId]?.byPlatform ?? {},
            loading: stillLoading,
          },
        },
      }))
    }
  },
}))

// CREATOR_MODE_MAP maps client-side CreativeType (e.g. 'instagram_post') to
// the backend platform key used for OAuth and publishing (e.g. 'instagram').
// That backend key is exactly LivePlatform — reuse the table instead of
// duplicating it.
const CREATIVE_TYPE_TO_PLATFORM = CREATOR_MODE_MAP as Record<string, LivePlatform>
export function creativeTypeToPlatform(type: Creative['type']): LivePlatform | null {
  return CREATIVE_TYPE_TO_PLATFORM[type] ?? null
}

const PLATFORM_TO_CREATIVE_TYPE = Object.fromEntries(
  Object.entries(CREATOR_MODE_MAP).map(([k, v]) => [v, k])
) as Record<LivePlatform, Creative['type']>

export function platformToCreativeType(p: LivePlatform): Creative['type'] | null {
  return PLATFORM_TO_CREATIVE_TYPE[p] ?? null
}
